import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import { motion } from 'framer-motion';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { getUrlFriendlyBlogId, getInternalPersonaId } from '../../utils/personaUtils';

const PersonaBlogsList = ({ user, onInteraction }) => {
  const { personaId: urlPersonaId } = useParams();
  const internalPersonaId = getInternalPersonaId(urlPersonaId);
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use your existing axios auth hook
  const axios = useAxiosAuth();
  // const defaultHeaders = {
  //   'Content-Type': 'application/json'
  // };

  const personaNameMap = {
  "mecenna": "Restoration Artisan",
  "DIYMind": "DIYMind",
  "houseimprovemnts": "Practical Craftsman",
  "wilkerdo": "Fearless Builder",
  "woodshopdiaries": "Everyday Woodsmith",
  "fixthisbuildthat": "Builder Marksman",
  "everydayhomerepair": "House Doctor"
  };


  useEffect(() => {
    const fetchBlogs = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Use the URL-friendly personaId for API call
        const response = await axios.get(`/blogs/${urlPersonaId}`);
        
        setBlogs(response.data.blogs);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setError('Failed to load articles. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchBlogs();
   // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [urlPersonaId, user, axios]);


const handleBlogClick = (blogId) => {
  if (!user) {
    // Use your existing interaction handler for auth
    onInteraction();
    return;
  }
  
  // Store the current persona ID before navigating
  localStorage.setItem('lastPersonaId', urlPersonaId);
  
  // Convert internal blog ID to URL-friendly format
  
  const urlFriendlyBlogId = getUrlFriendlyBlogId(blogId);
  navigate(`/blog/${urlFriendlyBlogId}`);
};

  // const handleBlogClick = (blogId) => {
  //   if (!user) {
  //     // Use your existing interaction handler for auth
  //     onInteraction();
  //     return;
  //   }
  //   navigate(`/blog/${blogId}`);
  // };

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 6,
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Container maxWidth="lg">
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography 
            variant="h1" 
            gutterBottom
            sx={{
              background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              fontWeight: 700
            }}
          >
             Articles by {personaNameMap[internalPersonaId] || urlPersonaId}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4 }}>
            Explore DIY projects and advice from this expert.
          </Typography>
          
          <Divider sx={{ mb: 4 }} />
        </Box>
        
        {blogs.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h6" color="text.secondary">
              No articles available from this expert yet.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/personal-projects')}
              sx={{ mt: 3 }}
            >
              Explore Other Experts
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {blogs.map((blog, index) => (
              <Grid item xs={12} md={6} key={blog.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => handleBlogClick(blog.id)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h5" component="div">
                        {blog.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {blog.blog ? 
                          blog.blog.substring(0, 200) + (blog.blog.length > 200 ? '...' : '') 
                          : 'Click to read this article'}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ p: 2 }}>
                      <Button 
                        size="small" 
                        variant="contained"
                        sx={{
                          boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                          },
                          transition: 'all 0.3s'
                        }}
                      >
                        Read Article
                      </Button>
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default PersonaBlogsList;